import React, { useState, useCallback } from 'react';
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import HistoryIcon from "@mui/icons-material/History";
import { Button, CreateTableNew as CreateTable, GeneralMessage, SearchBar, useGeneralMessage } from "../../../common/APICommon";
import { ViewEmployeeModal as ViewEmployee } from "../../Personnel/ViewEmployee";

// V2 Imports
import { useFilterManager } from '../../../v2/hooks/useFilterManager';
import { useEmployees } from '../../../v2/hooks/useEmployees';
import { DynamicFilterBar } from '../../../v2/components/filters/DynamicFilterBar';
import { fetchFullEmployeeDetails } from '../../Personnel/EmployeesListFld/EmployeesListApi';
import { LoadingSpinner } from '../../../common/Loading';
import { FEATURES } from '../../../config/features';
import { useEmployeeTransaction } from '../../../v2/hooks/useEmployeeTransaction';
import { EmployeeTransactionHistoryModal } from '../../../v2/components/modules/EmployeeTransactions/modal/components/EmployeeTransactionHistoryModal';

export const EmployeeTabs = ({ user, userAccessData, employeePhoto, setEmployeePhoto }) => {
  // 1. Manage Dynamic Filters
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    applyFilters,
    resetFilters,
    loadingEnums
  } = useFilterManager(FEATURES.EMPLOYEE_FILTERS); // must be similar with the DynamicFilterBar.requestedFilters

  // 1.1 Popup message setup
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // 2. Manage Employee Data & Search
  const {
    employees,
    loading: loadingData,
    handleSearch,
    refresh
  } = useEmployees(apiFilterPayload, loadingEnums);

  // 2.1 Get Employee Transaction Data (Selected Employee)
  const {
    selectedEmployeeHistoryData,
    loading: loadingEmployeeHistoryData,
    fetchEmployeeHistoryData,
    clearEmployeeHistoryData,
  } = useEmployeeTransaction({ user, userAccessData, showGeneralMessage });

  // 3. Local UI States for this feature
  const [currentPage, setCurrentPage] = useState(1);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [showViewEmployeeHistoryModal, setShowViewEmployeeHistoryModal] = useState(false);
  const [viewedData, setViewedData] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  const header = ["ID", "Full Name", "Department", "Position", "Actions"];
  const accessor = ["employeeId", "fullName", "employeeInformation.departmentDescription", "employeeInformation.positionDescription", "actions"];


  const actions = [
    {
      name: "View",
      tooltip: "View",
      icon: <RemoveRedEyeIcon fontSize="small" />,
      className: "flex items-center",
      function: (row) => viewEmployeeHandleClick(row),
      visible: true,
    },
    {
      name: "View History",
      tooltip: "View History",
      icon: <HistoryIcon fontSize="small" />,
      className: "flex items-center",
      function: (row) => viewEmployeeTransactionHistoryHandleClick(row),
      visible: true,
    },
  ];

  // Handlers
  const addHandleClick = () => { window.location.pathname = "/addEmployee"; };

  const viewEmployeeHandleClick = useCallback(async (data) => {
    setLoadingModal(true);
    try {
      const fullEmployeeData = await fetchFullEmployeeDetails(data.id);
      setViewedData(fullEmployeeData);
      setShowViewEmployee(true);
    } finally {
      setLoadingModal(false);
    }
  }, []);

  const viewEmployeeTransactionHistoryHandleClick = useCallback(async (data) => {
    setLoadingModal(true);
    try {
      // MODIFIED: Capture name and fetch data
      setSelectedEmployeeName(data.fullName);
      await fetchEmployeeHistoryData(data.id);
      setShowViewEmployeeHistoryModal(true);
    } finally {
      setLoadingModal(false);
    }
  }, [fetchEmployeeHistoryData]);

  const closeEmployeeTransactionHistory = () => {
    setShowViewEmployeeHistoryModal(false);
    setSelectedEmployeeName("");
    clearEmployeeHistoryData();
  }

  return (
    <div>
      {/* Dynamic Filter Section */}
      <div className="py-0 px-3">
        <div className="bg-white rounded-lg border border-gray-200 mt-2 p-4" style={{ border: "1px solid #d7d7d7" }}>
          <span className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4 block">Filter Personnel</span>
          <DynamicFilterBar
            requestedFilters={FEATURES.EMPLOYEE_FILTERS}
            activeFilterState={activeFilterState}
            onFilterChange={onFilterChange}
            onApply={applyFilters}
            onClear={resetFilters}
          />
        </div>
      </div>

      {/* Table Top Controls */}
      <div className="top-table row">
        <div className="left w-1/2">
          <SearchBar handleFilter={(val) => handleSearch(val)} />
        </div>
        <div className="right row w-1/2">
          <Button
            icon={<PeopleAltIcon fontSize="small" />}
            name={"ADD EMPLOYEE"}
            className="bg-customGreen hover:bg-customGreen text-white"
            onClick={addHandleClick}
          />
        </div>
      </div>

      {/* Employee Table */}
      <div id="container1" className="InfoList container px-2">
        <CreateTable
          tableName={"Employee List"}
          headers={header}
          accessors={accessor}
          data={employees}
          actions={actions}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={employees?.length}
        />
      </div>

      {/* Localized Modal */}
      {showViewEmployee && (
        <ViewEmployee
          user={user}
          isOpen={showViewEmployee}
          onClose={() => setShowViewEmployee(false)}
          viewedData={viewedData}
          setViewedData={setViewedData}
          fetchData={refresh}
          employeePhoto={employeePhoto}
          setEmployeePhoto={setEmployeePhoto}
        />
      )}
      {showViewEmployeeHistoryModal && (
        <EmployeeTransactionHistoryModal
          isOpen={showViewEmployeeHistoryModal}
          onClose={() => closeEmployeeTransactionHistory()}
          data={selectedEmployeeHistoryData}
          employeeName={selectedEmployeeName}
        />
      )}
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loadingData || loadingEnums || loadingModal || loadingEmployeeHistoryData} />
    </div>
  );
};